import { money } from '@/lib/money'
import type { UrgentTier } from '@/types'

/** Rows grouped by speed, tightest first; null ("any speed") last. */
function bySpeed(tiers: UrgentTier[]): [number | null, UrgentTier[]][] {
  const groups = new Map<number | null, UrgentTier[]>()
  for (const tier of tiers) groups.set(tier.max_days ?? null, [...(groups.get(tier.max_days ?? null) ?? []), tier])
  return [...groups.entries()].sort(([a], [b]) => (a == null ? 1 : b == null ? -1 : a - b))
}

/**
 * The per-piece rush rate for a product when the order carries `qty` pieces
 * of it (summed across every line of that product) and has `workingDays` to
 * be ready, mirroring PricingService::urgentRate on the server: the tightest
 * speed that still covers the days applies, a slower order takes the slowest
 * speed, and every piece pays the one rate its total falls in. Without
 * `workingDays` the slowest speed is read, the cheapest rush. Display only;
 * checkout and the manual order form take the rate from the delivery quote.
 *
 * Null when nothing covers it: the product is not offered urgent, or the
 * quantity is past a capped last range.
 */
export function urgentRateFor(tiers: UrgentTier[] | undefined, qty: number, workingDays?: number): number | null {
  const speeds = bySpeed(tiers ?? [])
  if (speeds.length === 0) return null

  const bounded = speeds.filter(([days]) => days != null)
  const group =
    (workingDays != null ? bounded.find(([days]) => (days as number) >= workingDays) : undefined) ??
    speeds.find(([days]) => days == null) ??
    bounded.at(-1)
  const row = group?.[1].find((t) => t.min_qty <= qty && (t.max_qty == null || qty <= t.max_qty))
  return row ? row.price : null
}

/** The largest quantity that can be rushed, or null when any quantity can (or none). */
export function urgentCap(tiers: UrgentTier[] | undefined): number | null {
  const sorted = [...(tiers ?? [])].sort((a, b) => a.min_qty - b.min_qty)
  return sorted.at(-1)?.max_qty ?? null
}

/** Why `qty` pieces cannot be rushed, or null when they can. */
export function rushBlocker(name: string, tiers: UrgentTier[] | undefined, qty: number): string | null {
  if (!tiers?.length) return `${name} is not offered as urgent production`
  if (urgentRateFor(tiers, qty) != null) return null
  return `${name} can only be produced urgent up to ${urgentCap(tiers)} pcs`
}

/**
 * "within 2 days: 1-50 pcs RM 8.00/pc, 51+ pcs RM 6.00/pc · within 4 days: RM
 * 4.00/pc" - one flat rate reads as just "RM 7.00/pc", and a speed is only
 * named when the rows have one.
 */
export function describeUrgentTiers(tiers: UrgentTier[]): string {
  return bySpeed(tiers)
    .map(([days, rows]) => {
      const sorted = [...rows].sort((a, b) => a.min_qty - b.min_qty)
      const prices =
        sorted.length === 1 && sorted[0].min_qty === 1 && sorted[0].max_qty == null
          ? `${money(sorted[0].price)}/pc`
          : sorted.map((t) => `${t.max_qty == null ? `${t.min_qty}+` : `${t.min_qty}-${t.max_qty}`} pcs ${money(t.price)}/pc`).join(', ')
      return days == null ? prices : `within ${days} working days: ${prices}`
    })
    .join(' · ')
}
